// Implements: F-UIX-016, F-UIX-017
/**
 * The collapsed conversation of the Lienzo editor (owner report, round 3): a narrow rail with the
 * way back and what happened while it was collapsed.
 *
 * The conversation stays mounted while collapsed (`EditorWorkspace`), so a turn keeps running; the
 * rail only reports it. "In progress" while a turn is open; "new messages" when the newest message
 * of the thread changed after the collapse. Expanding reads them: the next collapse starts from the
 * newest. A thread still loading (no newest yet) is never "new". The state is the editor's own, so it
 * belongs to one mockup (the editor is keyed by it) and is never persisted.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";

import type { ConversationActivity } from "../../modules/conversation/containers/ConversationContainer";
import { useTranslation } from "../../shared/i18n";
import { Button } from "../../shared/ui/atoms/Button";
import { Icon } from "../../shared/ui/atoms/Icon";
import styles from "./ConversationRail.module.css";

export interface ConversationRailProps {
  readonly turnOpen: boolean;
  readonly unread: boolean;
  readonly onExpand: () => void;
  readonly expandId?: string | undefined;
}

export function ConversationRail({ turnOpen, unread, onExpand, expandId }: ConversationRailProps): ReactElement {
  const { t } = useTranslation();
  const news = turnOpen ? t("editor.conversation.turnOpen") : unread ? t("editor.conversation.unread") : "";
  return (
    <>
      <Button
        id={expandId}
        variant="ghost"
        iconOnly
        label={t("editor.conversation.expand")}
        keyShortcuts="Shift+\"
        onClick={onExpand}
      >
        <Icon name="panel-left-open" />
      </Button>
      <span className={styles.news} role="status" title={news === "" ? undefined : news}>
        {news === "" ? null : (
          <>
            <span className={styles.dot} data-busy={turnOpen ? "true" : undefined} aria-hidden="true" />
            <span className={styles.srOnly}>{news}</span>
          </>
        )}
      </span>
    </>
  );
}

export interface RailActivity {
  readonly turnOpen: boolean;
  readonly unread: boolean;
  readonly onActivity: (activity: ConversationActivity) => void;
}

export function useRailActivity(collapsed: boolean): RailActivity {
  const [activity, setActivity] = useState<ConversationActivity>({ turnOpen: false, newestMessageId: null });
  const seen = useRef<string | null>(null);
  const [seenAtCollapse, setSeenAtCollapse] = useState<string | null>(null);
  // The newest message while expanded is read; collapsing freezes what was read.
  useEffect(() => {
    if (!collapsed) seen.current = activity.newestMessageId;
  }, [collapsed, activity.newestMessageId]);
  useEffect(() => {
    if (collapsed) setSeenAtCollapse(seen.current);
  }, [collapsed]);
  const onActivity = useCallback((next: ConversationActivity): void => {
    setActivity(next);
  }, []);
  const newest = activity.newestMessageId;
  return {
    turnOpen: activity.turnOpen,
    unread: collapsed && newest !== null && newest !== seenAtCollapse,
    onActivity,
  };
}
